import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { optimizedImage, imageSrcSet, PRODUCT_PLACEHOLDER, reportBrokenImage } from '../utils/productImage';
import { isAdminUser, subscribeSelection, toggleSelection } from '../utils/adminSelection';

export const GRADE_STYLE = { A: 'bg-emerald-600', B: 'bg-amber-500', C: 'bg-orange-600' };

const AdminSelectBox = ({ id, name }) => {
  const [checked, setChecked] = useState(false);
  useEffect(() => subscribeSelection((sel) => setChecked(sel.has(id))), [id]);
  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelection(id, name); }}
      title="Selectează pentru mutare în altă categorie"
      className={`absolute right-2 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-md border-2 text-xs font-bold transition-colors sm:right-3 sm:top-3 ${
        checked ? 'border-green-700 bg-green-700 text-white' : 'border-green-600 bg-white/90 text-transparent hover:text-green-700'
      }`}
      data-testid={`admin-select-${id}`}
    >
      ✓
    </button>
  );
};

const ProductCard = ({ product, onAddToCart, onAddToWishlist, onImageDead }) => {
  const { _id, name, price, oldPrice, rating, reviews, image, inStock, stock, isNew, discount, specialOrder } = product;
  const [adminMode] = useState(() => isAdminUser());
  const [imageDead, setImageDead] = useState(false);

  // Cerință client: niciun card fără poză reală. Dacă imaginea nu se încarcă, cardul dispare
  // și produsul e marcat în backend ca „imageBroken”, ca să nu mai apară în liste.
  const handleImageError = () => {
    setImageDead(true);
    reportBrokenImage(_id);
    onImageDead?.(_id);
  };

  const isInStock = stock > 0 || inStock === true;

  // Marja afișată adminului: marja proprie a produsului > marja aplicată > calcul din prețul de achiziție
  const marginPct = (() => {
    if (product.productMarkup != null && product.productMarkup !== '') return Number(product.productMarkup);
    if (product.appliedMarkup != null) return Number(product.appliedMarkup);
    const base = Number(product.basePrice || 0);
    if (base > 0) {
      const net = Number(product.priceWithoutVat || 0);
      const impliedGross = Number(price || 0) / base - 1;
      const impliedNet = net > 0 ? net / base - 1 : null;
      // unii furnizori trimit prețul de achiziție cu TVA inclus: alegem marja plauzibilă
      if (impliedNet === null) return impliedGross * 100;
      return (Math.abs(impliedNet) <= Math.abs(impliedGross) ? impliedNet : impliedGross) * 100;
    }
    return null;
  })();

  // pe prima pagină cardul cu poză moartă e înlocuit; în catalog primește imaginea generală R32
  if (!image || (imageDead && onImageDead)) return null;
  const showPlaceholder = imageDead || product.imageBroken;

  return (
    <Card data-product-id={_id} data-product-name={name} className="group relative flex h-full flex-col overflow-hidden rounded-2xl border-2 border-gray-100 transition-all duration-300 hover:border-green-500 hover:shadow-xl">
      {adminMode && <AdminSelectBox id={_id} name={name} />}
      {adminMode && (product.nameEn ? product.name === product.nameEn : product.source === 'babyproducts' && product.translated !== true) && (
        <span className="absolute left-2 top-2 z-20 rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow sm:left-3 sm:top-3"
          title="Produs netradus (descriere/nume în engleză)" data-testid={`admin-en-badge-${_id}`}>
          EN
        </span>
      )}
      <Link to={`/product/${_id}`} className="block">
        <div className="relative overflow-hidden rounded-t-2xl bg-white">
          <img
            src={showPlaceholder ? PRODUCT_PLACEHOLDER : optimizedImage(image, 400)}
            srcSet={showPlaceholder ? undefined : imageSrcSet(image, [200, 400])}
            sizes="(max-width: 640px) 45vw, 300px"
            alt={name}
            loading="lazy"
            decoding="async"
            onError={showPlaceholder ? undefined : handleImageError}
            data-testid={`product-card-image-${_id}`}
            className="h-36 w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105 sm:h-64 sm:p-4"
          />

          {/* Badges */}
          <div className="absolute left-2 top-2 flex flex-col gap-1 sm:left-3 sm:top-3 sm:gap-2">
            {discount > 0 && (
              <Badge className="rounded-lg bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white sm:rounded-xl sm:px-3 sm:py-1 sm:text-xs">
                -{discount}%
              </Badge>
            )}
            {isNew && (
              <Badge className="rounded-lg bg-green-600 px-1.5 py-0.5 text-[10px] font-bold text-white sm:rounded-xl sm:px-3 sm:py-1 sm:text-xs">
                NOU
              </Badge>
            )}
            {specialOrder ? (
              <Badge data-testid="cs-badge-card" className="rounded-lg bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold leading-tight text-white sm:rounded-xl sm:px-3 sm:py-1 sm:text-xs">
                COMANDĂ SPECIALĂ
              </Badge>
            ) : !isInStock && (
              <Badge className="rounded-lg bg-gray-500 px-1.5 py-0.5 text-[10px] font-bold text-white sm:rounded-xl sm:px-3 sm:py-1 sm:text-xs">
                STOC EPUIZAT
              </Badge>
            )}
          </div>

          {product.grade && (
            <span data-testid={`grade-badge-${product._id || product.id}`}
              className={`absolute bottom-2 left-2 z-10 rounded-lg px-2 py-1 text-xs font-extrabold uppercase tracking-wide text-white shadow-md sm:bottom-3 sm:left-3 sm:px-3 sm:py-1.5 sm:text-sm ${
                GRADE_STYLE[String(product.grade).charAt(0).toUpperCase()] || 'bg-gray-700'}`}>
              Grad {product.grade}
            </span>
          )}

          {/* Wishlist Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              onAddToWishlist?.(product);
            }}
            aria-label="Adaugă la favorite"
            className="absolute right-1.5 top-1.5 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 shadow-lg transition-colors hover:bg-red-50 sm:right-3 sm:top-3 sm:h-11 sm:w-11"
          >
            <Heart className="h-5 w-5 text-gray-600 hover:text-red-500" />
          </button>
        </div>
      </Link>

      <div className="flex flex-grow flex-col p-3 sm:p-4">
        <Link to={`/product/${_id}`}>
          <h3 className="mb-1.5 line-clamp-2 text-sm font-semibold leading-snug text-gray-900 transition-colors hover:text-green-600 sm:mb-2 sm:text-base">
            {name}
          </h3>
        </Link>

        {/* Rating */}
        <div className="mb-2 flex items-center sm:mb-3">
          <div className="flex items-center gap-0.5 sm:gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-3 w-3 sm:h-4 sm:w-4 ${
                  i < Math.floor(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="ml-1.5 text-xs text-gray-500 sm:ml-2 sm:text-sm">({reviews})</span>
        </div>

        {/* Price */}
        <div className="mb-3 flex items-end justify-between gap-2 sm:mb-4">
          <div>
          {specialOrder ? (
            <span className="text-xs font-semibold text-blue-700 sm:text-sm" data-testid="cs-mention-card">
              Furnizate la cerere, contactați R32.ro
            </span>
          ) : (
            <>
              {oldPrice > 0 && (
                <span className="mr-1.5 text-xs text-gray-400 line-through sm:mr-2 sm:text-sm">{oldPrice} Lei</span>
              )}
              <span className="text-lg font-bold text-green-600 sm:text-2xl">{price} Lei</span>
            </>
          )}
          </div>
          {adminMode && (
            <span
              title={product.productMarkup != null
                ? 'Marjă proprie a produsului'
                : 'Marjă din grupa de furnizor (calculată din prețul de achiziție)'}
              className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                product.productMarkup != null
                  ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'
                  : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
              }`}
              data-testid={`admin-margin-${_id}`}
            >
              {marginPct != null ? `M ${marginPct.toFixed(marginPct % 1 ? 1 : 0)}%` : 'M —'}
            </span>
          )}
        </div>

        <div className="flex-grow"></div>

        {/* Add to Cart / Special Order Button */}
        {specialOrder ? (
          <Link to="/contact" className="mt-auto" data-testid="cs-request-offer-btn">
            <Button className="min-h-[44px] w-full rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white transition-all hover:bg-blue-700 sm:py-6 sm:text-base">
              Cere ofertă
            </Button>
          </Link>
        ) : (
          <Button
            onClick={(e) => {
              e.preventDefault();
              onAddToCart?.(product);
            }}
            disabled={!isInStock}
            className="mt-auto min-h-[44px] w-full rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white transition-all hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300 sm:py-6 sm:text-base"
          >
            <ShoppingCart className="mr-1.5 h-4 w-4 sm:mr-2 sm:h-5 sm:w-5" />
            {isInStock ? 'Adaugă în Coș' : 'Indisponibil'}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default ProductCard;
